import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './OperatorInvoices.css';
import { formatCurrency } from '../utils/formatters';
import CreateInvoicePage from './CreateInvoicePage';

const API = '/api/invoices';
const CLIENTS_API = '/api/clients';
const ROUTES_API = '/api/routes';
const MASTER_DATA_API = '/api/master-data';
const UPLOAD_URL = '/api/uploads/';

/* ── Inline SVG Icons ── */
const Icon = ({ d, size = 16, strokeWidth = 1.75 }) => (
    <svg
        width={size} height={size} viewBox="0 0 24 24"
        fill="none" stroke="currentColor"
        strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
    >
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    plus: ['M12 5v14', 'M5 12h14'],
    search: ['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z', 'M21 21l-4.35-4.35'],
    edit: ['M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7', 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z'],
    trash: ['M3 6h18', 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2'],
    x: ['M18 6 6 18', 'M6 6l12 12'],
    check: ['M20 6 9 17l-5-5'],
    eye: ['M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z', 'M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z'],
    wallet: ['M20 12V8H6a2 2 0 0 1-2-2V5', 'M4 19v-2a2 2 0 0 1 2-2h14v3a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zm16-4V8'],
};

export default function OperatorInvoices({ user: propUser, operatorDetails }) {
    const [invoices, setInvoices] = useState([]);
    const [clients, setClients] = useState([]);
    const [routes, setRoutes] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedInvoiceToView, setSelectedInvoiceToView] = useState(null);
    const [statusModalOpen, setStatusModalOpen] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [createPageOpen, setCreatePageOpen] = useState(false);
    const [editItemForPage, setEditItemForPage] = useState(null);

    const user = propUser || JSON.parse(localStorage.getItem('ops-user'));
    const operatorId = user?.operatorId || (user?.userType === 'operator' ? user._id : 'all');
    const country = operatorDetails?.country;

    // Form State for Invoice Creation/Edit
    const [formData, setFormData] = useState({
        clientId: '',
        invoiceNumber: '',
        issueDate: new Date().toISOString().split('T')[0],
        dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        items: [{ routeId: '', routeName: '', tripsMonthly: 0, tripsDaily: 0, amountPerTrip: 0, totalAmount: 0 }],
        additionalAmount: 0,
        vatPercentage: 0,
        subject: '',
        description: '',
        notes: '',
        signatureName: 'Authorized Signatory'
    });


    // Form State for Status Update
    const [paymentData, setPaymentData] = useState({
        status: 'Paid',
        paidDate: new Date().toISOString().split('T')[0],
        paymentMethod: '',
        paymentNotes: '',
    });
    const [paymentFile, setPaymentFile] = useState(null);

    useEffect(() => {
        if (operatorId && operatorId !== 'all') fetchData();
    }, [operatorId]);



    const fetchData = async () => {
        if (!operatorId) return;
        setLoading(true);
        try {
            const [invRes, clientRes, routeRes, masterRes] = await Promise.all([
                axios.get(`${API}/${operatorId}`),
                axios.get(`${CLIENTS_API}/${operatorId}`),
                axios.get(`${ROUTES_API}/${operatorId}`),
                axios.get(MASTER_DATA_API)
            ]);
            setInvoices(invRes.data);
            setClients(clientRes.data);
            setRoutes(routeRes.data);

            // Extract payment methods from master data
            const methods = masterRes.data.filter(item => item.category === 'payment-methods');
            setPaymentMethods(methods);
        } catch (err) {
            console.error("Fetch error:", err);
        } finally {
            setLoading(false);
        }
    };


    const handleViewInvoice = (inv) => {
        setSelectedInvoiceToView(inv);
        setViewModalOpen(true);
    };

    const handlePrint = () => {
        const content = document.getElementById('printable-invoice');
        const printWindow = window.open('', '', 'width=900,height=650');
        printWindow.document.write('<html><head><title>Print Invoice</title>');
        printWindow.document.write('<style>body { font-family: sans-serif; padding: 20px;} .invoice-header { display: flex; justify-content: space-between; margin-bottom: 40px; } .invoice-table { width: 100%; border-collapse: collapse; margin-top: 20px; } .invoice-table th, .invoice-table td { border: 1px solid #ccc; padding: 10px; text-align: left; } .totals { width: 300px; margin-left: auto; margin-top: 20px; }</style>');
        printWindow.document.write('</head><body>');
        printWindow.document.write(content.innerHTML);
        printWindow.document.write('</body></html>');
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => { printWindow.print(); printWindow.close(); }, 250);
    };


    const handleOpenModal = (item = null) => {
        setEditItemForPage(item || null);
        setCreatePageOpen(true);
    };

    const handleOpenStatusModal = (inv) => {
        setSelectedInvoice(inv);
        setPaymentData({
            status: inv.status === 'Paid' ? 'Paid' : 'Paid',
            paidDate: inv.paidDate ? new Date(inv.paidDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
            paymentMethod: inv.paymentMethod || '',
            paymentNotes: inv.paymentNotes || '',
        });
        setPaymentFile(null);
        setStatusModalOpen(true);
    };


    const handleAddItem = () => {
        setFormData(prev => ({
            ...prev,
            items: [...prev.items, { routeId: '', routeName: '', tripsMonthly: 0, tripsDaily: 0, amountPerTrip: 0, totalAmount: 0 }]
        }));
    };

    const handleRemoveItem = (index) => {
        const newItems = [...formData.items];
        newItems.splice(index, 1);
        setFormData(prev => ({ ...prev, items: newItems }));
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...formData.items];
        const item = { ...newItems[index] };

        if (field === 'routeId') {
            const selectedRoute = routes.find(r => r._id === value);
            item.routeId = value;
            item.routeName = selectedRoute ? selectedRoute.name : '';
        } else {
            item[field] = value;
        }

        const totalTrips = (Number(item.tripsMonthly) || 0) + (Number(item.tripsDaily) || 0);
        item.totalAmount = totalTrips * (Number(item.amountPerTrip) || 0);

        newItems[index] = item;
        setFormData(prev => ({ ...prev, items: newItems }));
    };

    const calculateTotals = () => {
        const tripsSubtotal = formData.items.reduce((sum, item) => sum + (Number(item.totalAmount) || 0), 0);
        const subtotal = tripsSubtotal + (Number(formData.additionalAmount) || 0);
        const vatAmount = subtotal * (Number(formData.vatPercentage) / 100);
        const totalAmount = subtotal + vatAmount;

        return { tripsSubtotal, subtotal, vatAmount, totalAmount };
    };

    const { tripsSubtotal, subtotal, vatAmount, totalAmount } = calculateTotals();

    const handleSave = async (e) => {
        e.preventDefault();
        const data = {
            ...formData,
            operatorId,
            tripsSubtotal,
            subtotal,
            vatAmount,
            totalAmount
        };

        try {
            if (editItem) {
                await axios.put(`${API}/${editItem._id}`, data);
            } else {
                await axios.post(API, data);
            }
            setModalOpen(false);
            fetchData();
        } catch (err) {
            console.error("Save error:", err);
            alert("Failed to save invoice");
        }
    };

    const handleUpdateStatus = async (e) => {
        e.preventDefault();
        const data = new FormData();
        data.append('status', paymentData.status);
        if (paymentData.status === 'Paid') {
            data.append('paidDate', paymentData.paidDate);
            data.append('paymentMethod', paymentData.paymentMethod);
            data.append('paymentNotes', paymentData.paymentNotes);
            if (paymentFile) {
                data.append('paymentAttachment', paymentFile);
            }
        } else {
            data.append('paidDate', '');
            data.append('paymentMethod', '');
            data.append('paymentNotes', '');
        }

        try {
            await axios.put(`${API}/${selectedInvoice._id}`, data);
            setStatusModalOpen(false);
            fetchData();
        } catch (err) {
            console.error("Status update error:", err);
            alert("Failed to update status");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this invoice?")) return;
        try {
            await axios.delete(`${API}/${id}`);
            fetchData();
        } catch (err) {
            console.error("Delete error:", err);
        }
    };

    const filteredInvoices = invoices.filter(inv =>
        inv.invoiceNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inv.clientId?.companyName?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const pendingInvoicesCount = invoices.filter(inv => inv.status !== 'Paid').length;
    const paidInvoicesCount = invoices.filter(inv => inv.status === 'Paid').length;
    const totalRevenue = invoices.filter(inv => inv.status === 'Paid').reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);

    // If create/edit page is open, render it directly inside the operator layout (no fullscreen takeover)
    if (createPageOpen) {
        return (
            <div className="operator-page" style={{ padding: 0 }}>
                <CreateInvoicePage
                    user={propUser}
                    operatorDetails={operatorDetails}
                    editItem={editItemForPage}
                    onBack={() => setCreatePageOpen(false)}
                    onSaved={() => { setCreatePageOpen(false); fetchData(); }}
                />
            </div>
        );
    }

    return (
        <div className="operator-page">
            <div className="op-breadcrumb">
                <span className="home-icon">🏠</span>
                <span className="sep">›</span>
                <span>Finance</span>
                <span className="sep">›</span>
                <span className="current">Invoice</span>
            </div>

            <div className="op-header">
                <div className="op-header-left">
                    <h1 className="op-title">Billing & invoices</h1>
                    <p className="op-subtitle">View all your invoices and receipts and manage client billing</p>
                </div>
            </div>

            {/* Summary Grid */}
            <div className="op-stats-grid">
                <div className="op-stat-card">
                    <span className="op-stat-label">Pending Invoices</span>
                    <span className="op-stat-value">{pendingInvoicesCount}</span>
                </div>
                <div className="op-stat-card">
                    <span className="op-stat-label">Paid Invoices</span>
                    <span className="op-stat-value">{paidInvoicesCount}</span>
                </div>
                <div className="op-stat-card">
                    <span className="op-stat-label">Total Revenue</span>
                    <span className="op-stat-value">{formatCurrency(totalRevenue, country)}</span>
                </div>
            </div>

            <div className="op-actions-bar">
                <div className="op-search-box">
                    <span className="search-icon"><Icon d={icons.search} size={14} /></span>
                    <input
                        type="text"
                        placeholder="Search By Invoice # or Client Name"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <button className="op-create-btn" onClick={() => handleOpenModal()}>
                    <Icon d={icons.plus} size={14} />
                    Create Invoice
                </button>
            </div>

            <div className="op-table-container">
                <table className="op-table">
                    <thead>
                        <tr>
                            <th>INVOICE ID</th>
                            <th>CLIENTS</th>
                            <th>ISSUED DATE</th>
                            <th>DUE DATE</th>
                            <th>AMOUNT</th>
                            <th>STATUS</th>
                            <th>ACTIONS</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="7" style={{ textAlign: 'center', padding: '40px' }}>Loading invoices...</td></tr>
                        ) : filteredInvoices.length === 0 ? (
                            <tr><td colSpan="7" style={{ textAlign: 'center', padding: '40px' }}>No invoices found</td></tr>
                        ) : filteredInvoices.map((inv) => (
                            <tr key={inv._id}>
                                <td className="inv-id">
                                    <span className="checkbox-mini"></span>
                                    {inv.invoiceNumber}
                                </td>
                                <td>{inv.clientId?.companyName || 'Unknown'}</td>
                                <td>{new Date(inv.issueDate).toLocaleDateString()}</td>
                                <td>{new Date(inv.dueDate).toLocaleDateString()}</td>
                                <td className="inv-amount">{formatCurrency(inv.totalAmount, country)}</td>
                                <td>
                                    <span
                                        className={`status-badge ${inv.status.toLowerCase()}`}
                                    >
                                        {inv.status}
                                    </span>
                                </td>
                                <td className="inv-actions">
                                    <button title="View Invoice" className="payment-btn" onClick={() => handleViewInvoice(inv)} style={{ marginRight: '4px' }}>
                                        <Icon d={icons.eye} size={14} />
                                    </button>
                                    <button className="payment-btn" title="Change Status / Add Payment" onClick={() => handleOpenStatusModal(inv)}>
                                        <Icon d={icons.wallet} size={14} />
                                    </button>
                                    <button title="Edit" onClick={() => handleOpenModal(inv)}><Icon d={icons.edit} size={14} /></button>
                                    {inv.paymentAttachment && (
                                        <button title="View Receipt" onClick={() => window.open(`${UPLOAD_URL}${inv.paymentAttachment}`)}>
                                            <Icon d={icons.eye} size={14} />
                                        </button>
                                    )}
                                    <button title="Delete" className="delete-btn" onClick={() => handleDelete(inv._id)}><Icon d={icons.trash} size={14} /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* (legacy modal placeholder — replaced by inline page render above) */}

            {false && modalOpen && (
                <div className="invoice-modal-overlay" onClick={() => setModalOpen(false)}>
                    <div className="invoice-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="invoice-modal-header">
                            <h2>{editItem ? 'Edit Invoice' : 'Create New Invoice'}</h2>
                            <button className="close-btn" onClick={() => setModalOpen(false)}><Icon d={icons.x} size={20} /></button>
                        </div>
                        <form onSubmit={handleSave}>
                            <div className="invoice-modal-body">
                                <div className="invoice-form-grid">
                                    <div className="form-group">
                                        <label>Select Client</label>
                                        <select
                                            required
                                            value={formData.clientId}
                                            onChange={e => setFormData({ ...formData, clientId: e.target.value })}
                                        >
                                            <option value="">Select a Client</option>
                                            {clients.map(c => <option key={c._id} value={c._id}>{c.companyName}</option>)}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Invoice Number</label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.invoiceNumber}
                                            onChange={e => setFormData({ ...formData, invoiceNumber: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Issue Date</label>
                                        <input
                                            type="date"
                                            required
                                            value={formData.issueDate}
                                            onChange={e => setFormData({ ...formData, issueDate: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Due Date</label>
                                        <input
                                            type="date"
                                            required
                                            value={formData.dueDate}
                                            onChange={e => setFormData({ ...formData, dueDate: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                                        <label>Subject</label>
                                        <input
                                            type="text"
                                            placeholder="e.g. Monthly Transportation Services"
                                            value={formData.subject}
                                            onChange={e => setFormData({ ...formData, subject: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                                        <label>Description</label>
                                        <input
                                            type="text"
                                            placeholder="e.g. Services provided for the month of April 2026"
                                            value={formData.description}
                                            onChange={e => setFormData({ ...formData, description: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <div className="invoice-items-section">
                                    <div className="items-header">
                                        <h3>Invoice Items (Routes)</h3>
                                        <button type="button" className="add-item-btn" onClick={handleAddItem}>
                                            <Icon d={icons.plus} size={12} /> Add Route
                                        </button>
                                    </div>
                                    <div className="items-table-wrap">
                                        <table className="items-table">
                                            <thead>
                                                <tr>
                                                    <th>Route</th>
                                                    <th>Trips (Monthly)</th>
                                                    <th>Trips (Daily)</th>
                                                    <th>Amount / Trip</th>
                                                    <th>Total</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {formData.items.map((item, idx) => (
                                                    <tr key={idx}>
                                                        <td>
                                                            <select
                                                                required
                                                                value={item.routeId}
                                                                onChange={e => handleItemChange(idx, 'routeId', e.target.value)}
                                                            >
                                                                <option value="">Select Route</option>
                                                                {routes.map(r => <option key={r._id} value={r._id}>{r.name}</option>)}
                                                            </select>
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                value={item.tripsMonthly}
                                                                onChange={e => handleItemChange(idx, 'tripsMonthly', e.target.value)}
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                value={item.tripsDaily}
                                                                onChange={e => handleItemChange(idx, 'tripsDaily', e.target.value)}
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                step="0.001"
                                                                value={item.amountPerTrip}
                                                                onChange={e => handleItemChange(idx, 'amountPerTrip', e.target.value)}
                                                            />
                                                        </td>
                                                        <td className="row-total">
                                                            {formatCurrency(item.totalAmount, country)}
                                                        </td>
                                                        <td>
                                                            {formData.items.length > 1 && (
                                                                <button type="button" className="remove-row" onClick={() => handleRemoveItem(idx)}>
                                                                    <Icon d={icons.trash} size={12} />
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                <div className="invoice-totals-section">
                                    <div className="totals-title">Additional Charges & Totals</div>
                                    <div className="totals-form-grid">
                                        <div className="form-group">
                                            <label>Additional Amount (Optional)</label>
                                            <input
                                                type="number"
                                                step="0.001"
                                                placeholder="0.000"
                                                value={formData.additionalAmount}
                                                onChange={e => setFormData({ ...formData, additionalAmount: e.target.value })}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>VAT (%)</label>
                                            <select
                                                value={formData.vatPercentage}
                                                onChange={e => setFormData({ ...formData, vatPercentage: e.target.value })}
                                            >
                                                <option value="0">0%</option>
                                                <option value="5">5%</option>
                                                <option value="10">10%</option>
                                                <option value="15">15%</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="totals-display-box">
                                        <div className="total-row">
                                            <span>Trips Subtotal:</span>
                                            <span>{formatCurrency(tripsSubtotal, country)}</span>
                                        </div>
                                        <div className="total-row">
                                            <span>Additional Amount:</span>
                                            <span>{formatCurrency(Number(formData.additionalAmount) || 0, country)}</span>
                                        </div>
                                        <div className="total-row subtotal-line">
                                            <span>Subtotal:</span>
                                            <span className="bold">{formatCurrency(subtotal, country)}</span>
                                        </div>
                                        <div className="total-row">
                                            <span>VAT ({formData.vatPercentage}%):</span>
                                            <span>{formatCurrency(vatAmount, country)}</span>
                                        </div>
                                        <div className="total-row final-total">
                                            <span>Total Amount:</span>
                                            <span className="grand-total">{formatCurrency(totalAmount, country)}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="invoice-totals-section" style={{ marginTop: '20px' }}>
                                    <div className="totals-title">Additional Info</div>
                                    <div className="totals-form-grid" style={{ gridTemplateColumns: '1fr' }}>
                                        <div className="form-group">
                                            <label>Notes</label>
                                            <textarea
                                                rows="3"
                                                placeholder="Thank you for your business..."
                                                value={formData.notes}
                                                onChange={e => setFormData({ ...formData, notes: e.target.value })}
                                                style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '4px', fontFamily: 'inherit' }}
                                            ></textarea>
                                        </div>
                                        <div className="form-group">
                                            <label>Signature Name</label>
                                            <input
                                                type="text"
                                                placeholder="Authorized Signatory"
                                                value={formData.signatureName}
                                                onChange={e => setFormData({ ...formData, signatureName: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                </div>

                            </div>
                            <div className="invoice-modal-footer">
                                <button type="button" className="btn-cancel" onClick={() => setModalOpen(false)}>Cancel</button>
                                <button type="submit" className="btn-save">{editItem ? 'Update Invoice' : 'Create Invoice'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Status Update Modal */}
            {statusModalOpen && (
                <div className="invoice-modal-overlay" onClick={() => setStatusModalOpen(false)}>
                    <div className="invoice-modal-content small-modal" onClick={e => e.stopPropagation()}>
                        <div className="invoice-modal-header">
                            <h2>Update Invoice Status</h2>
                            <button className="close-btn" onClick={() => setStatusModalOpen(false)}><Icon d={icons.x} size={20} /></button>
                        </div>
                        <form onSubmit={handleUpdateStatus}>
                            <div className="invoice-modal-body">
                                <div className="form-group">
                                    <label>Status</label>
                                    <select
                                        value={paymentData.status}
                                        onChange={e => setPaymentData({ ...paymentData, status: e.target.value })}
                                    >
                                        <option value="Unpaid">Unpaid</option>
                                        <option value="Paid">Paid</option>
                                        <option value="Overdue">Overdue</option>
                                    </select>
                                </div>
                                {paymentData.status === 'Paid' && (
                                    <>
                                        <div className="form-group">
                                            <label>Payment Method</label>
                                            <select
                                                required
                                                value={paymentData.paymentMethod}
                                                onChange={e => setPaymentData({ ...paymentData, paymentMethod: e.target.value })}
                                            >
                                                <option value="">Select Payment Method</option>
                                                {paymentMethods.map(m => (
                                                    <option key={m._id} value={m.name}>{m.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>Payment Date</label>
                                            <input
                                                type="date"
                                                required
                                                value={paymentData.paidDate}
                                                onChange={e => setPaymentData({ ...paymentData, paidDate: e.target.value })}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Payment Notes</label>
                                            <textarea
                                                rows="3"
                                                placeholder="Enter payment reference or notes..."
                                                value={paymentData.paymentNotes}
                                                onChange={e => setPaymentData({ ...paymentData, paymentNotes: e.target.value })}
                                            ></textarea>
                                        </div>
                                        <div className="form-group">
                                            <label>Proof of Payment (Optional)</label>
                                            <input
                                                type="file"
                                                onChange={e => setPaymentFile(e.target.files[0])}
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className="invoice-modal-footer">
                                <button type="button" className="btn-cancel" onClick={() => setStatusModalOpen(false)}>Cancel</button>
                                <button type="submit" className="btn-save">Update Status</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* View Invoice Modal */}
            {viewModalOpen && selectedInvoiceToView && (
                <div className="invoice-modal-overlay" onClick={() => setViewModalOpen(false)}>
                    <div className="invoice-modal-content print-modal" onClick={e => e.stopPropagation()} style={{ width: '800px', padding: 0 }}>
                        <div className="invoice-modal-header" style={{ borderBottom: 'none', background: '#f8fafc', padding: '20px 30px' }}>
                            <h2 style={{ fontSize: '20px', margin: 0 }}>Invoice #{selectedInvoiceToView.invoiceNumber}</h2>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button type="button" className="btn-save" onClick={handlePrint}>Print PDF</button>
                                <button className="close-btn" onClick={() => setViewModalOpen(false)}><Icon d={icons.x} size={20} /></button>
                            </div>
                        </div>
                        <div className="invoice-modal-body" style={{ background: '#fff', padding: '0', maxHeight: '75vh', overflowY: 'auto' }}>
                            <div id="printable-invoice" style={{ padding: '40px', maxWidth: '800px', margin: '0 auto', fontFamily: 'Inter, sans-serif', color: '#1e293b' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '2px solid #e2e8f0', paddingBottom: '30px', marginBottom: '30px' }}>
                                    <div>
                                        {operatorDetails?.logo ? (
                                            <img src={`${UPLOAD_URL}${operatorDetails.logo}`} alt="Logo" style={{ maxHeight: '80px', maxWidth: '200px' }} />
                                        ) : (
                                            <h1 style={{ margin: 0, color: '#0f172a', fontSize: '28px' }}>INVOICE</h1>
                                        )}
                                        <div style={{ marginTop: '20px', color: '#64748b', fontSize: '14px', lineHeight: '1.6' }}>
                                            <strong>{operatorDetails?.companyName || 'Operator Company'}</strong><br />
                                            {operatorDetails?.address || ''}<br />
                                            {operatorDetails?.email || ''}<br />
                                            {operatorDetails?.phone || ''}
                                        </div>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <h2 style={{ fontSize: '32px', margin: 0, color: '#3b82f6', textTransform: 'uppercase', letterSpacing: '2px' }}>Invoice</h2>
                                        <div style={{ marginTop: '20px', fontSize: '14px', color: '#475569' }}>
                                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
                                                <span style={{ width: '100px', fontWeight: '600' }}>Invoice #:</span>
                                                <span style={{ width: '120px' }}>{selectedInvoiceToView.invoiceNumber}</span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
                                                <span style={{ width: '100px', fontWeight: '600' }}>Date:</span>
                                                <span style={{ width: '120px' }}>{new Date(selectedInvoiceToView.issueDate).toLocaleDateString()}</span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
                                                <span style={{ width: '100px', fontWeight: '600' }}>Due Date:</span>
                                                <span style={{ width: '120px' }}>{new Date(selectedInvoiceToView.dueDate).toLocaleDateString()}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '40px' }}>
                                    <div>
                                        <h3 style={{ fontSize: '12px', textTransform: 'uppercase', color: '#94a3b8', letterSpacing: '1px', marginBottom: '10px' }}>Bill To</h3>
                                        <div style={{ fontSize: '15px', color: '#1e293b', lineHeight: '1.6' }}>
                                            <strong style={{ fontSize: '16px', color: '#0f172a' }}>{selectedInvoiceToView.clientId?.companyName}</strong><br />
                                            {selectedInvoiceToView.clientId?.address}<br />
                                            {selectedInvoiceToView.clientId?.contactEmail}<br />
                                            {selectedInvoiceToView.clientId?.contactPhone}
                                        </div>
                                    </div>
                                </div>

                                {(selectedInvoiceToView.subject || selectedInvoiceToView.description) && (
                                    <div style={{ marginBottom: '30px' }}>
                                        {selectedInvoiceToView.subject && <h3 style={{ margin: '0 0 10px 0', color: '#0f172a', fontSize: '18px' }}>Subject: {selectedInvoiceToView.subject}</h3>}
                                        {selectedInvoiceToView.description && <p style={{ margin: 0, color: '#475569', fontSize: '14px', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>{selectedInvoiceToView.description}</p>}
                                    </div>
                                )}

                                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '30px' }}>
                                    <thead>
                                        <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                                            <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', textTransform: 'uppercase', color: '#64748b' }}>Description</th>
                                            <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '12px', textTransform: 'uppercase', color: '#64748b' }}>Qty</th>
                                            <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '12px', textTransform: 'uppercase', color: '#64748b' }}>Rate</th>
                                            <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '12px', textTransform: 'uppercase', color: '#64748b' }}>Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedInvoiceToView.items.map((item, idx) => (
                                            <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                                <td style={{ padding: '16px', color: '#334155' }}>
                                                    <div style={{ fontWeight: '500' }}>{item.routeId?.name || 'Route'}</div>
                                                </td>
                                                <td style={{ padding: '16px', textAlign: 'center', color: '#334155' }}>{item.totalTrips || ((item.monthlyTrips || 0) + (item.dailyTrips || 0))}</td>
                                                <td style={{ padding: '16px', textAlign: 'right', color: '#334155' }}>{formatCurrency(item.amountPerTrip, country)}</td>
                                                <td style={{ padding: '16px', textAlign: 'right', color: '#0f172a', fontWeight: '500' }}>{formatCurrency(item.totalAmount, country)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>

                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                                    <div style={{ width: '300px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e2e8f0', color: '#475569' }}>
                                            <span>Subtotal</span>
                                            <span>{formatCurrency(selectedInvoiceToView.subtotal || selectedInvoiceToView.totalAmount, country)}</span>
                                        </div>
                                        {(selectedInvoiceToView.additionalAmount > 0) && (
                                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e2e8f0', color: '#475569' }}>
                                                <span>Additional</span>
                                                <span>{formatCurrency(selectedInvoiceToView.additionalAmount, country)}</span>
                                            </div>
                                        )}
                                        {(selectedInvoiceToView.vatAmount > 0) && (
                                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e2e8f0', color: '#475569' }}>
                                                <span>VAT ({selectedInvoiceToView.vatPercentage || 0}%)</span>
                                                <span>{formatCurrency(selectedInvoiceToView.vatAmount, country)}</span>
                                            </div>
                                        )}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px 0', fontWeight: 'bold', fontSize: '18px', color: '#0f172a' }}>
                                            <span>Total</span>
                                            <span>{formatCurrency(selectedInvoiceToView.totalAmount, country)}</span>
                                        </div>
                                    </div>
                                </div>

                                {selectedInvoiceToView.notes && (
                                    <div style={{ marginTop: '40px', padding: '20px', background: '#f8fafc', borderRadius: '8px', color: '#475569', fontSize: '14px' }}>
                                        <strong>Notes:</strong><br />
                                        <p style={{ margin: '8px 0 0 0', whiteSpace: 'pre-wrap' }}>{selectedInvoiceToView.notes}</p>
                                    </div>
                                )}

                                <div style={{ marginTop: '50px', display: 'flex', justifyContent: 'flex-end', paddingBottom: '20px' }}>
                                    <div style={{ textAlign: 'center', width: '250px' }}>
                                        <div style={{ borderBottom: '1px solid #1e293b', marginBottom: '10px', height: '40px' }}></div>
                                        <strong style={{ fontSize: '14px', color: '#1e293b' }}>{selectedInvoiceToView.signatureName || 'Authorized Signatory'}</strong>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
